import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Input, message, notification, Typography } from 'antd';
import { ArrowRightOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import api from '../api/axios';
import AuthLayout from '../layouts/AuthLayout';

const { Title, Text } = Typography;

const Mfa = () => {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { mfa_token, mfa_type, redirectTo } = location.state || {};

    const handleOtpChange = (value) => {
        setOtp(value);
    };

    const handleOtpSubmit = async () => {
        if (otp.length !== 6) {
            message.error('Please enter a valid 6-digit OTP.');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/verify-mfa', { mfa_token, otp });

            if (response.status === 200 && response.data.status === 'success') {
                notification.success({ message: 'Login Successful', description: 'You have successfully logged in.' });
                localStorage.setItem('user_id', response.data.data.user.id);
                localStorage.setItem('first_name', response.data.data.user.first_name);
                localStorage.setItem('last_name', response.data.data.user.last_name);
                navigate(redirectTo || '/dashboard', { replace: true });
            } else {
                notification.error({ message: 'Login Failed', description: 'Please try again' });
            }
        } catch (error) {
            if (error.response && error.response.status === 422) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    notification.error({ message: 'Validation Error', description: errors[key][0] });
                });
            } else {
                const description = error.response?.data?.message || 'An error occurred. Please try again later.';
                notification.error({ message: 'Login Error', description });
            }
        } finally {
            setLoading(false);
        }
    };

    const mfaDescription =
        mfa_type === 'email' ? 'We sent a 6-digit code to your registered email address.' :
        mfa_type === 'sms' ? 'We sent a 6-digit code to your registered mobile number.' :
        mfa_type === 'authenticator' ? 'Enter the 6-digit code from your authenticator app.' :
        'Enter the 6-digit code sent to your email, mobile, or authenticator app.';

    return (
        <AuthLayout>
            <div style={{ width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                    <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg,#AD1457,#D81B60)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(173,20,87,0.3)' }}>
                        <SafetyCertificateOutlined style={{ fontSize: '1.6rem', color: 'white' }} />
                    </div>
                    <Title level={2} style={{ color: '#1A1A2E', marginBottom: '6px', fontWeight: '800', fontSize: '1.8rem' }}>
                        Verify Your Identity
                    </Title>
                    <Text style={{ color: '#64748B', fontSize: '0.95rem' }}>
                        {mfaDescription}
                    </Text>
                </div>

                <Form name="otp" layout="vertical" onFinish={handleOtpSubmit}>
                    <Form.Item>
                        <Input.OTP
                            value={otp}
                            onChange={handleOtpChange}
                            length={6}
                            isInputNum
                            autoFocus
                            formatter={(str) => str.toUpperCase()}
                            style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}
                            inputStyle={{ width: '48px', height: '56px', fontSize: '20px', fontWeight: '700', textAlign: 'center', borderRadius: '10px', border: '1.5px solid #E2E8F0' }}
                            focusStyle={{ border: '1.5px solid #AD1457', outline: 'none' }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: '12px' }}>
                        <Button type="primary" htmlType="submit" block loading={loading} icon={<ArrowRightOutlined />} style={{ height: '50px', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', background: 'linear-gradient(135deg,#AD1457,#D81B60)', border: 'none', boxShadow: '0 6px 20px rgba(173,20,87,0.35)' }}>
                            Verify & Sign In
                        </Button>
                    </Form.Item>
                </Form>

                <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid #F1F5F9' }}>
                    <Text style={{ color: '#94A3B8', fontSize: '0.78rem' }}>
                        {process.env.REACT_APP_RELEASE_DATE} · v{process.env.REACT_APP_RELEASE_VERSION}
                    </Text>
                </div>
            </div>
        </AuthLayout>
    );
};

export default Mfa;
